import { addonQuoteRequestApi } from 'apiConnector/quote/addonQuote';
import * as backendErrorHelper from 'epic/apiErrorEpicHelper';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics/tabRemoved';
import type { AddonQuoteRequestPayload } from 'models/quote/AddonQuoteRequestPayload';
import type { QuoteAck } from 'models/quote/QuoteAck';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { of } from 'rxjs';
import { catchError, map, mergeMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { addonQuoteAcked, addonQuoteRequestFailed } from 'state/addonForm/addonForm.action';
import * as actionTypes from 'state/addonForm/addonForm.actionTypes';
import type { EpicWithThunk } from 'state/state.common';
import { throwOnTimeout } from 'utils/rxjs';

export interface RequestAddonQuoteEpicApi {
  requestQuote(payload: AddonQuoteRequestPayload): Observable<QuoteAck>;
}

export function requestAddonQuoteEpic(appScheduler?: SchedulerLike): EpicWithThunk {
  return (action$, state$, { selectors: { addonSelector }, frontLogger, oldHttpClient, getSgmeConfiguration }) =>
    action$.pipe(
      ofType(actionTypes.ADDON_QUOTE_REQUESTED),
      withLatestFrom(state$),
      mergeMap(([{ tabId, quoteId: requestedQuoteId, quoteRequestStartingTimeInMillis }, state]) => {
        const request: AddonQuoteRequestPayload = addonSelector.getQuoteRequest(state, tabId, requestedQuoteId);
        const quoteAckTimeoutMs = getSgmeConfiguration().quoteAckTimeoutMs;

        const tabRemoved$ = tabRemoved(action$, tabId, state$);

        return addonQuoteRequestApi(oldHttpClient)
          .requestQuote(request)
          .pipe(
            throwOnTimeout(quoteAckTimeoutMs, new backendErrorHelper.TimeoutError(quoteAckTimeoutMs), appScheduler),
            takeUntil(tabRemoved$),
            map(({ quoteId, quoteExternalId, pricingEstimatedDurationInMilliseconds, isExecOnlyAddon }) =>
              addonQuoteAcked(
                tabId,
                quoteId,
                quoteExternalId,
                pricingEstimatedDurationInMilliseconds,
                quoteRequestStartingTimeInMillis,
                isExecOnlyAddon,
              ),
            ),
            catchError((error: Error) => {
              frontLogger.error('An error occurred during addon quote request {@FrontError}', error);
              return of(addonQuoteRequestFailed(tabId, backendErrorHelper.toApiErrorMessage(error), error));
            }),
          );
      }),
    );
}
